<template>
  <div
    data-component="agent-profile-popup"
    @click.stop
  >
    <agent-profile-popup-info
      :avatar="avatar"
      :agent="agent"
      :multi-user="multiUser"
    />

    <div class="divider" />

    <div class="options">
      <div
        v-for="option of options"
        :key="option.key"
        :data-option="option.key"
        :class="{ [`is-${option.status}`]: option.status }"
        @click="$emit('option-click', option.key)"
      >
        {{ option.label }}
      </div>

      <div v-if="options.length > 0" class="divider" />

      <theme-switcher />

      <div class="divider" />

      <div
        data-option="sign-out"
        @click="$emit('sign-out')"
      >
        {{ $t('HITL.common.labels.sign-out') }}
      </div>

      <div
        v-if="appVersion"
        class="app-version-label"
      >
        {{ $t('HITL.common.labels.app-version', { version: appVersion }) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import { AvatarOptions } from '@onereach/hitl-base-avatars';

import { IAgent, IAgentMultiUser } from '@/types';
import AgentProfilePopupInfo from './AgentProfilePopupInfo.vue';
import ThemeSwitcher from './ThemeSwitcher.vue';
import { AgentProfileOption } from '../../types';

export default defineComponent({
  name: 'AgentProfilePopup',
  components: { AgentProfilePopupInfo, ThemeSwitcher },
  props: {
    avatar: {
      type: Object as PropType<AvatarOptions>,
      default: undefined,
    },
    agent: {
      type: Object as PropType<IAgent>,
      required: true,
    },
    multiUser: {
      type: Object as PropType<IAgentMultiUser>,
      default: undefined,
    },
    options: {
      type: Array as PropType<AgentProfileOption[]>,
      default: () => [],
    },
    appVersion: {
      type: String,
      default: '',
    },
  },
  emits: ['option-click', 'sign-out'],
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='agent-profile-popup'] {
  @include basic-popup;
  display: flex;
  flex-direction: column;
  width: 22.5rem;

  .divider {
    flex: 0 0 1px;
    background: var(--or-c-surface-variant);
    margin-bottom: var(--s-2);
  }

  .options {
    display: flex;
    flex-direction: column;
    padding-bottom: var(--s-4);

    .divider {
      flex: 0 0 1px;
      background: var(--or-c-surface-variant);
      margin: var(--s-2) 0;
    }

    [data-option],
    .app-version-label {
      padding: 0 var(--s-5);
    }

    [data-option] {
      @include text-overflow;

      display: flex;
      align-items: center;
      height: 2.125rem;
      color: var(--or-c-on-background);
      font-size: var(--fs-1);
      cursor: pointer;

      &:hover {
        background: var(--or-c-primary-opacity-0-08);
      }

      /** TODO: Move to a new component when more colors are added */
      &.is-error {
        &::before {
          position: absolute;
          content: '';
          height: 0.5rem;
          width: 0.5rem;
          border-radius: 50%;
          margin-left: -0.875rem;
          background-color: var(--or-c-error);
        }
      }
    }

    .app-version-label {
      margin-top: var(--s-2);
      color: var(--or-c-outline);
      line-height: 1.5;
      font-size: var(--fs-0);
    }
  }
}
</style>
